import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useLanguage } from '@/lib/i18n';
import { useSalonProfile } from '@/contexts/SalonProfileContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Store, Bell, Receipt, Users, Database, Plus, Edit, Eye, EyeOff, Mail } from 'lucide-react';

const DEFAULT_SALON_PROFILE = {
  salonName: 'Black Shine',
  phone: '',
  email: '',
  address: '',
  gstNumber: '',
  website: '',
};

const DEFAULT_BILLING_SETTINGS = {
  invoicePrefix: 'INV-',
  nextInvoiceNumber: 1,
  loyaltyPointsPerRupee: 1,
  redemptionRatePerPoint: 0.5,
  bronzeMax: 2000,
  silverMax: 5000,
  goldMax: 10000,
};

const DEFAULT_NOTIFICATION_SETTINGS = {
  autoSendBookingConfirmation: true,
  autoSendBillWhatsApp: true,
  birthdayWishes: true,
  appointmentReminder: true,
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TAB_KEYS = [
  { id: 'profile', key: 'settings_salon_profile', icon: Store },
  { id: 'notifications', key: 'settings_notifications', icon: Bell },
  { id: 'billing', key: 'settings_billing', icon: Receipt },
  { id: 'users', key: 'settings_user_mgmt', icon: Users },
  { id: 'data', key: 'settings_data_backup', icon: Database },
];

function downloadCsv(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

const TAB_IDS = ['profile', 'notifications', 'billing', 'users', 'data'];

export default function Settings() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(() => (TAB_IDS.includes(tabFromUrl) ? tabFromUrl : 'profile'));
  const [salonForm, setSalonForm] = useState(DEFAULT_SALON_PROFILE);
  const [billingForm, setBillingForm] = useState(DEFAULT_BILLING_SETTINGS);
  const [notifForm, setNotifForm] = useState(DEFAULT_NOTIFICATION_SETTINGS);
  const [accessStaff, setAccessStaff] = useState([]);
  const [accessStaffLoading, setAccessStaffLoading] = useState(false);
  const [enableLoginOpen, setEnableLoginOpen] = useState(false);
  const [enableStaffId, setEnableStaffId] = useState(null);
  const [enableForm, setEnableForm] = useState({ email: '', password: '' });
  const [showEnablePwd, setShowEnablePwd] = useState(false);
  const [removeAccessConfirmOpen, setRemoveAccessConfirmOpen] = useState(false);
  const [removeAccessStaffId, setRemoveAccessStaffId] = useState(null);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [lastBackupDate, setLastBackupDate] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [resendApiKeyInput, setResendApiKeyInput] = useState('');
  const [resendApiKeyMasked, setResendApiKeyMasked] = useState('');
  const [resendKeyTouched, setResendKeyTouched] = useState(false);
  const [showResendKey, setShowResendKey] = useState(false);
  const [resendApiKeyError, setResendApiKeyError] = useState('');
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const { refreshProfile } = useSalonProfile();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (TAB_IDS.includes(tab)) setActiveTab(tab);
  }, [searchParams]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/salon-profile');
        const p = res.data || {};
        setSalonForm({
          salonName: p.salon_name ?? p.salonName ?? DEFAULT_SALON_PROFILE.salonName,
          phone: p.phone ?? '',
          email: p.email ?? '',
          address: p.address ?? '',
          gstNumber: p.gst_number ?? '',
          website: p.website ?? '',
        });
        const tt = p.tier_thresholds || {};
        setBillingForm({
          invoicePrefix: p.invoice_prefix ?? DEFAULT_BILLING_SETTINGS.invoicePrefix,
          nextInvoiceNumber: p.next_invoice_number ?? DEFAULT_BILLING_SETTINGS.nextInvoiceNumber,
          loyaltyPointsPerRupee: p.loyalty_points_per_rupee ?? DEFAULT_BILLING_SETTINGS.loyaltyPointsPerRupee,
          redemptionRatePerPoint: p.redemption_rate_per_point ?? DEFAULT_BILLING_SETTINGS.redemptionRatePerPoint,
          bronzeMax: tt.bronze_max ?? DEFAULT_BILLING_SETTINGS.bronzeMax,
          silverMax: tt.silver_max ?? DEFAULT_BILLING_SETTINGS.silverMax,
          goldMax: tt.gold_max ?? DEFAULT_BILLING_SETTINGS.goldMax,
        });
        setNotifForm({
          autoSendBookingConfirmation: p.auto_send_booking_confirmation ?? DEFAULT_NOTIFICATION_SETTINGS.autoSendBookingConfirmation,
          autoSendBillWhatsApp: p.auto_send_bill_whatsapp ?? DEFAULT_NOTIFICATION_SETTINGS.autoSendBillWhatsApp,
          birthdayWishes: p.birthday_wishes ?? DEFAULT_NOTIFICATION_SETTINGS.birthdayWishes,
          appointmentReminder: p.appointment_reminder ?? DEFAULT_NOTIFICATION_SETTINGS.appointmentReminder,
        });
        const raw = p.resend_api_key ?? '';
        setResendApiKeyMasked(typeof raw === 'string' && raw.includes('*') ? raw : '');
        setResendApiKeyInput('');
        setResendKeyTouched(false);
        setResendApiKeyError('');
      } catch {
        toast.error(t('settings_load_error'));
      } finally {
        setProfileLoading(false);
      }
    };
    load();
  }, []);

  const handleSaveSalon = async () => {
    if (!salonForm.salonName?.trim()) {
      toast.error(t('settings_salon_name') + ' ' + t('settings_field_required'));
      return;
    }
    try {
      await api.put('/salon-profile', {
        salon_name: salonForm.salonName.trim(),
        phone: salonForm.phone?.trim() || '',
        email: salonForm.email?.trim() || '',
        address: salonForm.address?.trim() || '',
        gst_number: salonForm.gstNumber?.trim() || '',
        website: salonForm.website?.trim() || '',
      });
      refreshProfile();
      toast.success(t('settings_saved'));
    } catch {
      toast.error(t('settings_save_error'));
    }
  };

  const getTierThresholdError = () => {
    const b = Math.max(0, Math.floor(Number(billingForm.bronzeMax) || 0));
    const s = Math.max(0, Math.floor(Number(billingForm.silverMax) || 0));
    const g = Math.max(0, Math.floor(Number(billingForm.goldMax) || 0));
    if (b >= s) return t('tier_threshold_error', { higher: t('tier_silver'), lower: t('tier_bronze') });
    if (s >= g) return t('tier_threshold_error', { higher: t('tier_gold'), lower: t('tier_silver') });
    return null;
  };

  const handleSaveBilling = async () => {
    const err = getTierThresholdError();
    if (err) {
      toast.error(err);
      return;
    }
    const b = Math.max(0, Math.floor(Number(billingForm.bronzeMax) || 0));
    const s = Math.max(0, Math.floor(Number(billingForm.silverMax) || 0));
    const g = Math.max(0, Math.floor(Number(billingForm.goldMax) || 0));
    const data = {
      invoice_prefix: billingForm.invoicePrefix?.trim() || 'INV-',
      next_invoice_number: Math.max(1, Number(billingForm.nextInvoiceNumber) || 1),
      loyalty_points_per_rupee: Math.max(0, Number(billingForm.loyaltyPointsPerRupee) ?? 1),
      redemption_rate_per_point: Math.max(0, Number(billingForm.redemptionRatePerPoint) ?? 0.5),
      tier_thresholds: { bronze_max: b, silver_max: s, gold_max: g },
    };
    try {
      await api.put('/salon-profile', data);
      setBillingForm({
        ...billingForm,
        invoicePrefix: data.invoice_prefix,
        nextInvoiceNumber: data.next_invoice_number,
        loyaltyPointsPerRupee: data.loyalty_points_per_rupee,
        redemptionRatePerPoint: data.redemption_rate_per_point,
        bronzeMax: b,
        silverMax: s,
        goldMax: g,
      });
      refreshProfile();
      toast.success(t('settings_saved'));
    } catch {
      toast.error(t('settings_save_error'));
    }
  };

  const validateResendKey = (value) => {
    if (!value || !value.trim()) return '';
    const v = value.trim();
    if (!v.startsWith('re_')) return t('resend_api_key_invalid');
    if (v.length < 20) return t('resend_api_key_invalid');
    return '';
  };

  const handleSaveNotifications = async () => {
    const keyToSend = resendApiKeyInput.trim();
    if (keyToSend) {
      const error = validateResendKey(keyToSend);
      if (error) {
        setResendApiKeyError(error);
        return;
      }
    }
    setResendApiKeyError('');
    const payload = {
      auto_send_booking_confirmation: !!notifForm.autoSendBookingConfirmation,
      auto_send_bill_whatsapp: !!notifForm.autoSendBillWhatsApp,
      birthday_wishes: !!notifForm.birthdayWishes,
      appointment_reminder: !!notifForm.appointmentReminder,
    };
    if (resendKeyTouched) {
      if (resendApiKeyMasked && !keyToSend) {
        payload.resend_api_key = '';
      } else if (keyToSend) {
        payload.resend_api_key = keyToSend;
      }
    }
    try {
      await api.put('/salon-profile', payload);
      if (payload.resend_api_key !== undefined) {
        setResendApiKeyInput('');
        setResendKeyTouched(false);
        setResendApiKeyMasked(keyToSend ? 're_**********************' + keyToSend.slice(-3) : '');
      }
      refreshProfile();
      toast.success(payload.resend_api_key !== undefined ? t('resend_key_saved') : t('settings_saved'));
    } catch {
      toast.error(t('settings_save_error'));
    }
  };

  const handleTestEmail = async () => {
    setTestEmailLoading(true);
    try {
      await api.post('/salon-profile/test-email');
      toast.success(t('test_email_success'));
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || t('test_email_failed');
      toast.error(Array.isArray(msg) ? msg[0] : msg);
    } finally {
      setTestEmailLoading(false);
    }
  };

  const loadAccessStaff = async () => {
    setAccessStaffLoading(true);
    try {
      const res = await api.get('/staff');
      setAccessStaff(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error(t('staff_load_error'));
      setAccessStaff([]);
    } finally {
      setAccessStaffLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'users') loadAccessStaff();
  }, [activeTab]);

  const openEnableLoginDialog = (s) => {
    setEnableStaffId(s.id);
    setEnableForm({ email: s.login_email || '', password: '' });
    setShowEnablePwd(false);
    setEnableLoginOpen(true);
  };

  const handleEnableLoginSave = async () => {
    const em = enableForm.email.trim();
    const pw = enableForm.password.trim();
    if (!em || !EMAIL_REGEX.test(em)) {
      toast.error(t('val_email_invalid'));
      return;
    }
    if (!pw || pw.length < 8) {
      toast.error(t('val_password_min'));
      return;
    }
    if (!enableStaffId) return;
    try {
      await api.post(`/staff/${enableStaffId}/enable-login`, { email: em, password: pw });
      toast.success(t('staff_updated'));
      setEnableLoginOpen(false);
      loadAccessStaff();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || t('settings_save_error');
      toast.error(Array.isArray(msg) ? msg[0] : msg);
    }
  };

  const openRemoveAccessConfirm = (staffId) => {
    setRemoveAccessStaffId(staffId);
    setRemoveAccessConfirmOpen(true);
  };

  const handleRemoveStaffAccess = async () => {
    if (!removeAccessStaffId) return;
    try {
      await api.delete(`/staff/${removeAccessStaffId}/login`);
      toast.success(t('staff_updated'));
      setRemoveAccessConfirmOpen(false);
      setRemoveAccessStaffId(null);
      loadAccessStaff();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || t('settings_save_error');
      toast.error(Array.isArray(msg) ? msg[0] : msg);
    }
  };

  const handleFullBackup = async () => {
    setBackupLoading(true);
    const date = new Date().toISOString().slice(0, 10);
    try {
      const [customersRes, bookingsRes, servicesRes, staffRes] = await Promise.all([
        api.get('/customers'),
        api.get('/bookings'),
        api.get('/services'),
        api.get('/staff'),
      ]);
      const customers = customersRes.data;
      const bookings = bookingsRes.data;
      const services = servicesRes.data;
      const staff = staffRes.data;

      const toCsv = (arr) => {
        if (!Array.isArray(arr) || arr.length === 0) return '""';
        const h = Object.keys(arr[0]).filter(k => k !== '_id');
        if (!h.length) return '""';
        const rows = arr.map(r => h.map(k => `"${String(r[k] ?? '').replace(/"/g, '""')}"`).join(','));
        return [h.join(','), ...rows].join('\n');
      };
      downloadCsv(toCsv(customers), `salonsync_customers_${date}.csv`);
      await new Promise(r => setTimeout(r, 1000));
      downloadCsv(toCsv(bookings), `salonsync_bookings_${date}.csv`);
      await new Promise(r => setTimeout(r, 1000));
      downloadCsv(toCsv(services), `salonsync_services_${date}.csv`);
      await new Promise(r => setTimeout(r, 1000));
      downloadCsv(toCsv(staff), `salonsync_staff_${date}.csv`);

      setLastBackupDate(new Date().toISOString());
      toast.success(t('settings_backup_complete'));
    } catch (e) {
      toast.error(t('settings_backup_failed') + ': ' + (e.message || t('settings_try_again')));
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreConfirm = () => {
    setRestoreConfirmOpen(false);
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = (e) => {
      const file = e.target?.files?.[0];
      if (!file) return;
      const name = file.name.toLowerCase();
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = ev.target?.result || '';
        const key = name.includes('customer') ? 'salonsync_backup_customers' : name.includes('booking') ? 'salonsync_backup_bookings' : name.includes('service') ? 'salonsync_backup_services' : name.includes('staff') ? 'salonsync_backup_staff' : null;
        if (key) {
          localStorage.setItem(key, text);
          toast.success(t('settings_restore_success'));
        } else {
          toast.error(t('settings_restore_unknown'));
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
      {/* Left: Tab navigation - vertical on desktop */}
      <div className="lg:w-[280px] flex-shrink-0">
        {/* Mobile: horizontal scrollable pills */}
        <div className="lg:hidden overflow-x-auto pb-2 -mx-1">
          <div className="flex gap-2 min-w-max px-1">
            {TAB_KEYS.map((tab) => {
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setSearchParams({ tab: tab.id }); }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-[#f0a500] text-[#1a1a2e] shadow-lg shadow-[#f0a500]/20'
                      : 'bg-white border border-gray-200 text-gray-600 hover:bg-[#faf8f5]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t(tab.key)}
                </button>
              );
            })}
          </div>
        </div>
        {/* Desktop: vertical sidebar-style tabs */}
        <div className="hidden lg:block space-y-1">
          {TAB_KEYS.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSearchParams({ tab: tab.id }); }}
                className={`flex items-center gap-3 w-full px-4 py-3.5 rounded-xl text-base font-medium transition-all duration-200 text-left ${
                  isActive
                    ? 'bg-[#f0a500] text-[#1a1a2e] shadow-lg shadow-[#f0a500]/20'
                    : 'text-gray-600 hover:bg-[#faf8f5]'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {t(tab.key)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Right: Content panel */}
      <div className="flex-1 min-w-0">
        {profileLoading && (
          <Card className="border-0 shadow-md rounded-2xl">
            <CardContent className="p-6 space-y-4">
              <Skeleton className="h-12 w-full rounded-xl" />
              <Skeleton className="h-12 w-full rounded-xl" />
              <Skeleton className="h-12 w-full rounded-xl" />
              <Skeleton className="h-24 w-full rounded-xl" />
            </CardContent>
          </Card>
        )}
        {!profileLoading && activeTab === 'profile' && (
          <Card className="border-0 shadow-md rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="font-poppins text-lg">{t('settings_salon_profile')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="text-sm font-medium mb-2 block">{t('settings_salon_name')} *</Label>
                <Input
                  className="h-12 rounded-xl"
                  placeholder={t('settings_salon_name')}
                  value={salonForm.salonName}
                  onChange={(e) => setSalonForm((f) => ({ ...f, salonName: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">{t('settings_phone')}</Label>
                <Input
                  className="h-12 rounded-xl"
                  placeholder={t('settings_phone')}
                  value={salonForm.phone}
                  onChange={(e) => setSalonForm((f) => ({ ...f, phone: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">{t('settings_email')}</Label>
                <Input
                  type="email"
                  className="h-12 rounded-xl"
                  placeholder={t('settings_email')}
                  value={salonForm.email}
                  onChange={(e) => setSalonForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">{t('settings_address')}</Label>
                <Textarea
                  className="min-h-[80px] rounded-xl"
                  rows={3}
                  placeholder={t('settings_address')}
                  value={salonForm.address}
                  onChange={(e) => setSalonForm((f) => ({ ...f, address: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">{t('settings_gst')}</Label>
                <Input
                  className="h-12 rounded-xl"
                  placeholder="22AAAAA0000A1Z5"
                  value={salonForm.gstNumber}
                  onChange={(e) => setSalonForm((f) => ({ ...f, gstNumber: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">{t('settings_website')}</Label>
                <Input
                  type="url"
                  className="h-12 rounded-xl"
                  placeholder="https://"
                  value={salonForm.website}
                  onChange={(e) => setSalonForm((f) => ({ ...f, website: e.target.value }))}
                />
              </div>
              <Button
                onClick={handleSaveSalon}
                className="w-full h-12 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] font-semibold"
              >
                {t('settings_save')}
              </Button>
            </CardContent>
          </Card>
        )}

        {!profileLoading && activeTab === 'billing' && (
          <Card className="border-0 shadow-md rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="font-poppins text-lg">{t('settings_billing')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="text-sm font-medium mb-2 block">{t('settings_inv_prefix')}</Label>
                <Input
                  className="h-12 rounded-xl"
                  placeholder="INV-"
                  value={billingForm.invoicePrefix}
                  onChange={(e) => setBillingForm((f) => ({ ...f, invoicePrefix: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">{t('settings_next_inv')}</Label>
                <Input
                  type="number"
                  min={1}
                  className="h-12 rounded-xl"
                  value={billingForm.nextInvoiceNumber}
                  onChange={(e) => setBillingForm((f) => ({ ...f, nextInvoiceNumber: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">{t('settings_loyalty_per_rupee')}</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.1}
                  className="h-12 rounded-xl"
                  value={billingForm.loyaltyPointsPerRupee}
                  onChange={(e) => setBillingForm((f) => ({ ...f, loyaltyPointsPerRupee: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">{t('settings_redemption_per_point')}</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.1}
                  className="h-12 rounded-xl"
                  value={billingForm.redemptionRatePerPoint}
                  onChange={(e) => setBillingForm((f) => ({ ...f, redemptionRatePerPoint: e.target.value }))}
                />
              </div>

              {/* Loyalty Tiers */}
              <div className="pt-4 border-t border-gray-200 space-y-3">
                <h4 className="font-poppins font-semibold text-base text-[#1a1a2e]">{t('loyalty_tiers')}</h4>
                <p className="text-sm text-gray-500">{t('loyalty_tiers_subtitle')}</p>
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left py-2.5 px-3 font-medium text-[#1a1a2e]">{t('tier_table_tier')}</th>
                        <th className="text-left py-2.5 px-3 font-medium text-[#1a1a2e]">{t('tier_min_points')}</th>
                        <th className="text-left py-2.5 px-3 font-medium text-[#1a1a2e]">{t('tier_max_points')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="py-2.5 px-3">🥉 {t('tier_bronze')}</td>
                        <td className="py-2.5 px-3">0</td>
                        <td className="py-2 px-3">
                          <Input
                            type="number"
                            min={0}
                            className="h-9 rounded-lg w-24"
                            value={billingForm.bronzeMax}
                            onChange={(e) => setBillingForm((f) => ({ ...f, bronzeMax: e.target.value }))}
                          />
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2.5 px-3">🥈 {t('tier_silver')}</td>
                        <td className="py-2.5 px-3 text-gray-500">{t('tier_min_auto')}</td>
                        <td className="py-2 px-3">
                          <Input
                            type="number"
                            min={0}
                            className="h-9 rounded-lg w-24"
                            value={billingForm.silverMax}
                            onChange={(e) => setBillingForm((f) => ({ ...f, silverMax: e.target.value }))}
                          />
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2.5 px-3">🥇 {t('tier_gold')}</td>
                        <td className="py-2.5 px-3 text-gray-500">{t('tier_min_auto')}</td>
                        <td className="py-2 px-3">
                          <Input
                            type="number"
                            min={0}
                            className="h-9 rounded-lg w-24"
                            value={billingForm.goldMax}
                            onChange={(e) => setBillingForm((f) => ({ ...f, goldMax: e.target.value }))}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2.5 px-3">💎 {t('tier_diamond')}</td>
                        <td className="py-2.5 px-3 text-gray-500">{t('tier_min_auto')}</td>
                        <td className="py-2.5 px-3 text-gray-600">∞</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {getTierThresholdError() && (
                  <p className="text-sm text-red-600">{getTierThresholdError()}</p>
                )}
                <p className="text-sm text-gray-600">
                  {t('tier_preview')}: {t('tier_bronze')} (0–{(Number(billingForm.bronzeMax) || 0).toLocaleString('en-IN')}) → {t('tier_silver')} ({(Number(billingForm.bronzeMax) || 0) + 1}–{(Number(billingForm.silverMax) || 0).toLocaleString('en-IN')}) → {t('tier_gold')} ({(Number(billingForm.silverMax) || 0) + 1}–{(Number(billingForm.goldMax) || 0).toLocaleString('en-IN')}) → {t('tier_diamond')} ({(Number(billingForm.goldMax) || 0) + 1}+)
                </p>
                <div>
                  <p className="text-sm font-medium text-[#1a1a2e] mb-2">{t('tier_badges_preview')}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: '#FDE8D0', color: '#92400E' }}>🥉 {t('tier_bronze')}</span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: '#F1F5F9', color: '#475569' }}>🥈 {t('tier_silver')}</span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: '#FEF3C7', color: '#92600A' }}>🥇 {t('tier_gold')}</span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: '#EDE9FE', color: '#5B21B6' }}>💎 {t('tier_diamond')}</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleSaveBilling}
                className="w-full h-12 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] font-semibold"
              >
                {t('settings_save')}
              </Button>
            </CardContent>
          </Card>
        )}

        {!profileLoading && activeTab === 'notifications' && (
          <Card className="border-0 shadow-md rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="font-poppins text-lg">{t('settings_notifications')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {[
                { key: 'autoSendBookingConfirmation', titleKey: 'notif_booking_title', subKey: 'notif_booking_sub' },
                { key: 'autoSendBillWhatsApp', titleKey: 'notif_bill_title', subKey: 'notif_bill_sub' },
                { key: 'birthdayWishes', titleKey: 'notif_birthday_title', subKey: 'notif_birthday_sub' },
                { key: 'appointmentReminder', titleKey: 'notif_reminder_title', subKey: 'notif_reminder_sub' },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between gap-4 p-4 bg-[#faf8f5] rounded-xl">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1a1a2e]">{t(item.titleKey)}</p>
                    <p className="text-sm text-gray-500 mt-0.5">{t(item.subKey)}</p>
                  </div>
                  <Switch
                    checked={!!notifForm[item.key]}
                    onCheckedChange={(v) => setNotifForm(f => ({ ...f, [item.key]: v }))}
                    className="data-[state=checked]:bg-[#f0a500] data-[state=unchecked]:bg-gray-300"
                  />
                </div>
              ))}
              <p className="text-sm text-gray-500">
                {t('whatsapp_note')}
              </p>

              {/* Email Configuration */}
              <div className="p-4 bg-[#faf8f5] rounded-xl space-y-3 border border-gray-100">
                <p className="font-medium text-[#1a1a2e] flex items-center gap-2">
                  <Mail className="w-4 h-4" /> {t('email_config')}
                </p>
                <div>
                  <Label className="text-sm font-medium mb-2 block">{t('resend_api_key')}</Label>
                  <div className="flex gap-2 items-start">
                    <div className="relative flex-1">
                      <Input
                        type={showResendKey ? 'text' : 'password'}
                        className="h-12 rounded-xl pr-12"
                        placeholder={resendApiKeyMasked || t('resend_api_key_placeholder')}
                        value={resendApiKeyInput}
                        onFocus={() => {
                          setResendKeyTouched(true);
                          if (resendApiKeyMasked) setResendApiKeyInput('');
                        }}
                        onChange={(e) => {
                          setResendKeyTouched(true);
                          setResendApiKeyInput(e.target.value);
                          if (resendApiKeyError) setResendApiKeyError('');
                        }}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => setShowResendKey(!showResendKey)}
                        aria-label={showResendKey ? 'Hide key' : 'Show key'}
                      >
                        {showResendKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-12 rounded-xl border-gray-300 shrink-0"
                      onClick={handleTestEmail}
                      disabled={testEmailLoading}
                    >
                      {testEmailLoading ? t('test_email_sending') : t('test_email')}
                    </Button>
                  </div>
                  {resendApiKeyError && (
                    <p className="text-sm text-red-600 mt-1">{resendApiKeyError}</p>
                  )}
                  {!resendApiKeyMasked && !resendApiKeyInput && (
                    <p className="text-sm text-gray-500 mt-1">{t('using_system_email')}</p>
                  )}
                </div>
                <p className="text-sm text-gray-500">{t('resend_api_key_help')}</p>
                <a
                  href="https://resend.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#f0a500] hover:underline inline-flex items-center gap-1"
                >
                  {t('resend_open')}
                </a>
                <p className="text-sm text-gray-600">✅ {t('resend_used_for')}</p>
              </div>

              <Button
                onClick={handleSaveNotifications}
                className="w-full h-12 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] font-semibold"
              >
                {t('settings_save')}
              </Button>
            </CardContent>
          </Card>
        )}

        {!profileLoading && activeTab === 'users' && (
          <Card className="border-0 shadow-md rounded-2xl">
            <CardHeader className="pb-3 space-y-1">
              <CardTitle className="font-poppins text-lg">{t('staff_app_access_title')}</CardTitle>
              <p className="text-sm text-gray-500 font-normal">{t('staff_app_access_subtitle')}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {accessStaffLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-10 w-full rounded-xl" />
                  <Skeleton className="h-10 w-full rounded-xl" />
                  <Skeleton className="h-10 w-full rounded-xl" />
                  <Skeleton className="h-10 w-full rounded-xl" />
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-xl border border-gray-100">
                    <table className="w-full min-w-[560px] text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b border-gray-100 bg-[#faf8f5]">
                          <th className="px-4 py-3 font-medium">{t('staff_table_member')}</th>
                          <th className="px-4 py-3 font-medium">{t('staff_role')}</th>
                          <th className="px-4 py-3 font-medium">{t('app_access')}</th>
                          <th className="px-4 py-3 font-medium text-right">{t('settings_table_actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accessStaff.map((s) => (
                          <tr key={s.id} className="border-b border-gray-50 last:border-0">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-[#1a1a2e] text-white flex items-center justify-center font-bold shrink-0">
                                  {s.name?.[0] || 'S'}
                                </div>
                                <div>
                                  <p className="font-medium text-[#1a1a2e] flex flex-wrap items-center gap-2">
                                    {s.name}
                                    {s.is_current_session_user && (
                                      <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-[#f0a500]/20 text-[#1a1a2e]">
                                        ({t('settings_you_badge')})
                                      </span>
                                    )}
                                  </p>
                                  <Badge variant="secondary" className="mt-1 text-xs font-normal">
                                    {s.role}
                                  </Badge>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top">{s.role}</td>
                            <td className="px-4 py-3 align-top">
                              {s.has_login ? (
                                <span className="text-green-800 text-xs sm:text-sm">
                                  {t('app_access_active_line', { email: s.login_email || '—' })}
                                </span>
                              ) : (
                                <span className="text-gray-500">{t('app_access_no_access')}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right align-top">
                              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-1">
                                {!s.has_login ? (
                                  <Button variant="outline" size="sm" className="rounded-lg text-xs sm:text-sm" onClick={() => openEnableLoginDialog(s)}>
                                    {t('enable_login')}
                                  </Button>
                                ) : (
                                  <>
                                    <Button variant="ghost" size="sm" className="rounded-lg gap-1" onClick={() => navigate(`/staff?edit=${encodeURIComponent(s.id)}`)}>
                                      <Edit className="w-4 h-4" /> {t('edit')}
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="rounded-lg text-red-600 hover:text-red-700 text-xs sm:text-sm"
                                      onClick={() => openRemoveAccessConfirm(s.id)}
                                    >
                                      {t('disable_login')}
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {accessStaff.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-6">{t('staff_none')}</p>
                  )}
                  <Button variant="outline" onClick={() => navigate('/staff?addStaff=1')} className="rounded-xl gap-2 border-gray-300 w-full sm:w-auto">
                    <Plus className="w-4 h-4" /> {t('add_staff_member')}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {!profileLoading && activeTab === 'data' && (
          <Card className="border-0 shadow-md rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="font-poppins text-lg">{t('settings_data_backup')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-3">
                <Button
                  variant="outline"
                  onClick={handleFullBackup}
                  disabled={backupLoading}
                  className="w-full h-14 rounded-xl border-2 border-[#1a1a2e] text-[#1a1a2e] gap-2 font-medium"
                >
                  📥 {t('settings_backup_full')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setRestoreConfirmOpen(true)}
                  className="w-full h-14 rounded-xl border-2 border-[#f0a500] text-[#f0a500] hover:bg-[#f0a500]/10 gap-2 font-medium"
                >
                  📤 {t('settings_restore_btn')}
                </Button>
              </div>
              <p className="text-sm text-gray-500">
                {t('settings_last_backup')}: {lastBackupDate ? new Date(lastBackupDate).toLocaleString() : t('settings_never')}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={enableLoginOpen} onOpenChange={setEnableLoginOpen}>
        <DialogContent className="bg-[#faf8f5] rounded-2xl max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-poppins text-base sm:text-lg">{t('enable_login')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 sm:mt-4">
            <div>
              <Label className="text-sm mb-1.5 block">{t('login_email')} *</Label>
              <Input
                type="email"
                className="h-11 rounded-xl"
                value={enableForm.email}
                onChange={(e) => setEnableForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="staff@email.com"
              />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">{t('login_password')} *</Label>
              <div className="relative">
                <Input
                  type={showEnablePwd ? 'text' : 'password'}
                  className="h-11 rounded-xl pr-12"
                  value={enableForm.password}
                  onChange={(e) => setEnableForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder={t('login_password_placeholder')}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowEnablePwd(!showEnablePwd)}
                >
                  {showEnablePwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <div className="flex justify-end pt-4 gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setEnableLoginOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleEnableLoginSave} className="h-11 px-6 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] font-semibold">
              {t('save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Restore Confirmation */}
      <AlertDialog open={restoreConfirmOpen} onOpenChange={setRestoreConfirmOpen}>
        <AlertDialogContent className="rounded-2xl max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-poppins">{t('settings_restore_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings_restore_confirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestoreConfirm} className="bg-red-500 hover:bg-red-600 rounded-xl">{t('settings_restore_yes')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={removeAccessConfirmOpen} onOpenChange={(open) => { if (!open) setRemoveAccessStaffId(null); setRemoveAccessConfirmOpen(open); }}>
        <AlertDialogContent className="rounded-2xl max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-poppins">{t('disable_login')}</AlertDialogTitle>
            <AlertDialogDescription>{t('remove_app_access_confirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveStaffAccess} className="bg-red-500 hover:bg-red-600 rounded-xl">
              {t('disable_login')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
